import { FC, useMemo } from 'react';
import { Button, Col, Divider, Popconfirm, Space } from 'antd';
import _ from 'lodash';
import { TextQuote } from 'lucide-react';
import { useQueryState } from 'nuqs';
import { TbArrowUpRight, TbCircleDashedX, TbInfoCircle, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    SEARCH_HORIZONTAL_GUTTER,
    type SearchTabKey,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/searchTabs';
import { useTags, useWatchlists } from '@/queries';
import {
    buildSearchFilterEnvelope,
    DropdownButton,
    FilterMatchingPatternSelect,
    FilterRowEmptyState,
    FilterRowSurface,
    getAdvancedFilterCounts,
    getAdvancedFiltersCounts,
    getSearchAdvancedFilters,
    getSearchFilterJoin,
    searchFiltersQuery,
    SmallTooltip,
    unwrapSearchFilters,
    updateSearchAdvancedFilters,
    updateSearchFilterEnvelope,
    type SearchFilterChangeHandler,
} from '@/shared';

import { getFiltersRowEntries } from './filterEntries';
import { getSearchFilterFieldMetadata } from './filterFieldMetadata';
import { useSearchFilterValueOptions } from './filterValueOptions';
import AttributeFilterItem from './options/AttributeFilterItem';
import PremiumOrEventFilterItem from './options/PremiumOrEventFilterItem';
import {
    applySearchFilterTemplate,
    searchFilterTemplates,
    type SearchFilterTemplateDefinition,
} from './recommendations';
import SearchFilterDropdown from './SearchFilterDropdown';

interface FiltersRowProps {
    handleChange: SearchFilterChangeHandler;
    includeRoot?: boolean;
    scopePaths?: string[][];
    tab: SearchTabKey;
}

const FiltersRow: FC<FiltersRowProps> = ({ handleChange, includeRoot = true, scopePaths = [], tab }): JSX.Element => {
    const [filters, setFilters] = useQueryState('filters', searchFiltersQuery);

    const handleDelete = (path: string) => {
        setFilters((prev: unknown) =>
            updateSearchFilterEnvelope(prev, (currentFilters) => {
                const newFilters = _.cloneDeep(currentFilters);

                _.unset(newFilters, path);

                const pruneEmpty = (value: unknown): unknown => {
                    if (_.isArray(value)) {
                        return value.filter((item) => pruneEmpty(item) !== undefined);
                    }
                    if (_.isPlainObject(value)) {
                        const cleaned = Object.entries(value as Record<string, unknown>).reduce(
                            (accumulator, [key, nestedValue]) => {
                                const prunedValue = pruneEmpty(nestedValue);

                                if (prunedValue !== undefined) {
                                    accumulator[key] = prunedValue;
                                }

                                return accumulator;
                            },
                            {} as Record<string, unknown>,
                        );

                        return Object.keys(cleaned).length ? cleaned : undefined;
                    }

                    return value === '' || value === null || value === undefined ? undefined : value;
                };

                return (pruneEmpty(newFilters) as Record<string, unknown> | undefined) ?? {};
            }),
        );
    };

    const { token } = useAntDesignTheme();
    const { data: tags = [] } = useTags();
    const { data: watchlists = [] } = useWatchlists();
    const primitiveValueOptions = useMemo(
        () => ({
            workspaceTags: tags.map((tag) => ({ label: tag.name, value: tag.id })),
            watchlists: watchlists
                .filter((watchlist) => !watchlist.archived)
                .map((watchlist) => ({ label: watchlist.title, value: watchlist.id })),
        }),
        [tags, watchlists],
    );
    const primitiveOptionsFor = (path: string, key: string) => {
        if (key === 'workspaceTags' || path.endsWith('workspace.tags')) {
            return primitiveValueOptions.workspaceTags;
        }
        if (key === 'watchlists') {
            return primitiveValueOptions.watchlists;
        }

        return undefined;
    };

    // For premium filters (imports) & event filters (news, etc.), just set as "Premium Filters Enabled", or "M&A Feed Enabled"

    const legacyFilters = unwrapSearchFilters(filters);
    const filterEntries = getFiltersRowEntries(legacyFilters, includeRoot, scopePaths);
    const advancedFilters = getSearchAdvancedFilters(filters);
    const advancedFilterCounts = getAdvancedFiltersCounts(advancedFilters);
    const advancedFilterEntries = filterEntries.filter((entry) => !entry.group);
    const eventFilterEntries = filterEntries.filter((entry) => entry.group === 'eventFilters');
    const premiumFilterEntries = filterEntries.filter((entry) => entry.group === 'premiumFilters');
    const activeFilterCount = filterEntries.length + advancedFilterCounts.conditions;
    const matchingPattern = getSearchFilterJoin(filters);
    const templates = searchFilterTemplates[tab];
    const filterValueOptions = useSearchFilterValueOptions(advancedFilterEntries.map((entry) => entry.path));

    const handleApplyTemplate = (template: SearchFilterTemplateDefinition) => {
        void setFilters((previousFilters: unknown) =>
            updateSearchFilterEnvelope(previousFilters, (currentFilters) =>
                unwrapSearchFilters(applySearchFilterTemplate(currentFilters, tab, template)),
            ),
        );
    };

    return (
        <FilterRowSurface horizontalPadding={SEARCH_HORIZONTAL_GUTTER}>
            {activeFilterCount !== 0 ? (
                <>
                    <Col flex='auto'>
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 4,
                                minWidth: 0, // prevent overflow bugs
                            }}
                        >
                            <SearchFilterDropdown
                                activeCount={activeFilterCount}
                                filters={filters}
                                handleChange={handleChange}
                                key={tab}
                                onAdvancedFilterChange={(definition) => {
                                    if (!definition) return;
                                    void setFilters((previousFilters: unknown) =>
                                        updateSearchAdvancedFilters(previousFilters, [
                                            ...getSearchAdvancedFilters(previousFilters),
                                            definition,
                                        ]),
                                    );
                                }}
                                tab={tab}
                            />
                            <Divider type='vertical' />
                            {advancedFilters.map((advancedFilter, advancedFilterIndex) => {
                                const counts = getAdvancedFilterCounts(advancedFilter);

                                return (
                                    <SearchFilterDropdown
                                        key={`${advancedFilter.groups[0]?.id ?? 'advanced-filter'}-${advancedFilterIndex}`}
                                        activeCount={activeFilterCount}
                                        advancedFilter={advancedFilter}
                                        filters={filters}
                                        handleChange={handleChange}
                                        onAdvancedFilterChange={(definition) => {
                                            if (!definition) return;
                                            void setFilters((previousFilters: unknown) => {
                                                const next = [...getSearchAdvancedFilters(previousFilters)];
                                                next[advancedFilterIndex] = definition;
                                                return updateSearchAdvancedFilters(previousFilters, next);
                                            });
                                        }}
                                        openToAdvancedFilters
                                        tab={tab}
                                        trigger={
                                            <span>
                                                <Space.Compact size='small'>
                                                    <DropdownButton size='small'>
                                                        <TextQuote size='0.8125rem' />
                                                        <small>
                                                            Advanced Filter · {counts.groups}{' '}
                                                            {counts.groups === 1 ? 'Group' : 'Groups'} ·{' '}
                                                            {counts.conditions}{' '}
                                                            {counts.conditions === 1 ? 'Condition' : 'Conditions'}
                                                        </small>
                                                    </DropdownButton>
                                                    <Button
                                                        aria-label='Clear Advanced Filters'
                                                        icon={<TbX style={{ color: token.colorTextTertiary }} />}
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            void setFilters((previousFilters: unknown) =>
                                                                updateSearchAdvancedFilters(
                                                                    previousFilters,
                                                                    getSearchAdvancedFilters(previousFilters).filter(
                                                                        (_, index) => index !== advancedFilterIndex,
                                                                    ),
                                                                ),
                                                            );
                                                        }}
                                                        size='small'
                                                    />
                                                </Space.Compact>
                                            </span>
                                        }
                                    />
                                );
                            })}
                            {advancedFilterEntries.map((entry) => {
                                const fieldMetadata = getSearchFilterFieldMetadata(entry.path);

                                return (
                                    <AttributeFilterItem
                                        fieldLabel={fieldMetadata?.label}
                                        handleDelete={handleDelete}
                                        key={entry.path}
                                        label={entry.key}
                                        path={entry.path}
                                        value={entry.value}
                                        valueOptionsLoading={filterValueOptions.isLoading(entry.path)}
                                        valueOptions={
                                            primitiveOptionsFor(entry.path, entry.key) ??
                                            fieldMetadata?.options ??
                                            filterValueOptions.optionsFor(entry.path)
                                        }
                                        valueSelectionMode={fieldMetadata?.selectionMode}
                                    />
                                );
                            })}
                            {eventFilterEntries.map((entry) => (
                                <PremiumOrEventFilterItem
                                    key={entry.path}
                                    label={entry.key}
                                    path={entry.path}
                                    value={entry.value}
                                    filters={legacyFilters}
                                    handleDelete={handleDelete}
                                />
                            ))}
                            {premiumFilterEntries.map((entry) => (
                                <PremiumOrEventFilterItem
                                    key={entry.path}
                                    label={entry.key}
                                    path={entry.path}
                                    value={entry.value}
                                    filters={legacyFilters}
                                    handleDelete={handleDelete}
                                />
                            ))}
                        </div>
                    </Col>
                    <Col flex='none' style={{ alignItems: 'center', display: 'flex' }}>
                        {filterEntries.length >= 2 && (
                            <>
                                <FilterMatchingPatternSelect
                                    value={matchingPattern}
                                    onChange={(nextPattern) =>
                                        void setFilters((previousFilters: unknown) =>
                                            buildSearchFilterEnvelope(previousFilters, nextPattern),
                                        )
                                    }
                                />
                                <Divider type='vertical' />
                            </>
                        )}
                    </Col>
                    <Col flex='none'>
                        <Popconfirm
                            placement='bottomRight'
                            title='Are you sure you want to clear these filters?'
                            overlayStyle={{ width: 240 }}
                            okText='Yes'
                            onConfirm={() => {
                                setFilters((previousFilters: unknown) =>
                                    updateSearchFilterEnvelope(
                                        updateSearchAdvancedFilters(previousFilters, []),
                                        (currentFilters) => {
                                            const nextFilters = _.cloneDeep(currentFilters);

                                            filterEntries.forEach((entry) => _.unset(nextFilters, entry.path));

                                            return nextFilters;
                                        },
                                    ),
                                );
                            }}
                        >
                            <Button size='small' type='text'>
                                <TbCircleDashedX
                                    style={{
                                        color: token.colorTextTertiary,
                                    }}
                                />
                                <small
                                    style={{
                                        color: token.colorTextTertiary,
                                    }}
                                >
                                    Clear
                                </small>
                            </Button>
                        </Popconfirm>
                    </Col>
                </>
            ) : (
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <FilterRowEmptyState
                        entryAction={
                            <SearchFilterDropdown
                                activeCount={activeFilterCount}
                                filters={filters}
                                handleChange={handleChange}
                                key={tab}
                                onAdvancedFilterChange={(definition) => {
                                    if (!definition) return;
                                    void setFilters((previousFilters: unknown) =>
                                        updateSearchAdvancedFilters(previousFilters, [
                                            ...getSearchAdvancedFilters(previousFilters),
                                            definition,
                                        ]),
                                    );
                                }}
                                tab={tab}
                            />
                        }
                        hint={
                            <>
                                Apply filters to refine search results
                                <SmallTooltip
                                    placement='rightTop'
                                    padding='0 0.5rem'
                                    title={
                                        <>
                                            You can learn more about our search filters & functionality in{' '}
                                            <a
                                                href='https://docs.countercyclical.io/fundamentals/search'
                                                target='_blank'
                                                rel='noopener noreferrer'
                                            >
                                                our docs
                                                <TbArrowUpRight style={{ verticalAlign: '-0.125rem' }} />
                                            </a>
                                        </>
                                    }
                                >
                                    <TbInfoCircle style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }} />
                                </SmallTooltip>
                            </>
                        }
                        recommendations={templates.map((template) => {
                            const { Icon, key, label } = template;

                            return {
                                icon: <Icon aria-hidden size='0.75rem' />,
                                key,
                                label,
                                onClick: () => handleApplyTemplate(template),
                            };
                        })}
                    />
                </Col>
            )}
        </FilterRowSurface>
    );
};

export default FiltersRow;
